/**
 * The "new production" dialog (PLAN §5.1, D22, D32): a studio and a show style
 * (pre-selected: the studio's default; both snapshotted into the production
 * now), what it runs — one rundown, or a playlist of them — and whether it is
 * a rehearsal. The choice is local UI
 * state, fresh on every opening; the request is the container's.
 */
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Field, FieldError } from '../ui/field.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { shownShowStyle } from '../../lib/showStyle/pick.js';
import { ShowStylePicker } from '../showStyles/ShowStylePicker.js';

export type Choice = { readonly id: string; readonly name: string };
/** A studio to pick, with the show style it pre-selects (D32). */
export type StudioChoice = Choice & { readonly defaultShowStyleId: string | null };

export type ProductionRequest = {
  readonly studioId: string;
  /** The show style it runs (D32). */
  readonly showStyleId: string;
  readonly source: { readonly kind: 'rundown'; readonly rundownId: string } | { readonly kind: 'playlist'; readonly playlistId: string };
  readonly rehearsal: boolean;
};

export type CreateProductionDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly studios: readonly StudioChoice[];
  readonly showStyles: readonly Choice[];
  readonly rundowns: readonly Choice[];
  readonly playlists: readonly Choice[];
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (request: ProductionRequest) => void;
};

function SourcePicker(props: {
  readonly kind: 'rundown' | 'playlist';
  readonly sources: readonly Choice[];
  readonly sourceId: string;
  readonly disabled: boolean;
  readonly onKind: (kind: 'rundown' | 'playlist') => void;
  readonly onSource: (id: string) => void;
}) {
  const { kind, sources, sourceId, disabled } = props;
  const noun = kind === 'rundown' ? 'Rundown' : 'Playlist';
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-2 text-sm font-medium">Runs</legend>
      <div className="flex gap-4 text-sm">
        {(['rundown', 'playlist'] as const).map((k) => (
          <label key={k} className="flex items-center gap-1.5">
            <input type="radio" name="production-source-kind" checked={kind === k} disabled={disabled} onChange={() => props.onKind(k)} />
            {k === 'rundown' ? 'A rundown' : 'A playlist'}
          </label>
        ))}
      </div>
      <NativeSelect aria-label={noun} value={sourceId} onChange={(e) => props.onSource(e.target.value)} disabled={disabled || sources.length === 0}>
        {sources.length === 0 ? <option value="">No {noun.toLowerCase()}s</option> : null}
        {sources.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </NativeSelect>
    </fieldset>
  );
}

function CreateProductionForm({ onOpenChange, studios, showStyles, rundowns, playlists, pending, error, onSubmit }: Omit<CreateProductionDialogProps, 'open'>) {
  const [pickedStudio, setStudioId] = useState('');
  const [pickedShowStyle, setShowStyleId] = useState('');
  const [kind, setKind] = useState<'rundown' | 'playlist'>(rundowns.length > 0 || playlists.length === 0 ? 'rundown' : 'playlist');
  const [pickedSource, setSourceId] = useState('');
  const [rehearsal, setRehearsal] = useState(false);
  const sources = kind === 'rundown' ? rundowns : playlists;
  // What is shown selected: the pick, else the first choice — the lists may arrive after the dialog opened.
  const studioId = studios.some((s) => s.id === pickedStudio) ? pickedStudio : (studios[0]?.id ?? '');
  const studioDefault = studios.find((s) => s.id === studioId)?.defaultShowStyleId ?? null;
  const showStyleId = shownShowStyle(pickedShowStyle, studioDefault, showStyles);
  const sourceId = sources.some((s) => s.id === pickedSource) ? pickedSource : (sources[0]?.id ?? '');
  const ready = studioId !== '' && showStyleId !== '' && sourceId !== '';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit({ studioId, showStyleId, source: kind === 'rundown' ? { kind, rundownId: sourceId } : { kind, playlistId: sourceId }, rehearsal });
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New production</DialogTitle>
        <DialogDescription>The studio and show style are copied into the production now; later edits to either do not change it.</DialogDescription>
      </DialogHeader>
      <Field>
        <Label htmlFor="production-studio">Studio</Label>
        <NativeSelect
          id="production-studio"
          value={studioId}
          onChange={(e) => {
            setStudioId(e.target.value);
            // Another studio: its default show style, until the user picks again.
            setShowStyleId('');
          }}
          disabled={pending || studios.length === 0}
        >
          {studios.length === 0 ? <option value="">No studios — create one first</option> : null}
          {studios.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <ShowStylePicker
        id="production-show-style"
        label="Show style"
        showStyles={showStyles}
        value={showStyleId}
        studioDefault={studioDefault}
        allowNone={false}
        disabled={pending}
        description={null}
        onChange={setShowStyleId}
      />
      <SourcePicker
        kind={kind}
        sources={sources}
        sourceId={sourceId}
        disabled={pending}
        onKind={(k) => {
          setKind(k);
          setSourceId('');
        }}
        onSource={setSourceId}
      />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={rehearsal} onChange={(e) => setRehearsal(e.target.checked)} disabled={pending} />
        Rehearsal — playout runs, nothing airs for real
      </label>
      <FieldError>{error}</FieldError>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="create-production-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateProductionDialog({ open, ...rest }: CreateProductionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={rest.onOpenChange}>
      <DialogContent data-testid="create-production-dialog">
        <CreateProductionForm {...rest} />
      </DialogContent>
    </Dialog>
  );
}
